import { Link, useParams } from '@tanstack/react-router';
import { useGetCandidate, ApiError } from '@sara/api-client';
import { keepPreviousData } from '@tanstack/react-query';
import { Skeleton } from '@sara/ui/components/skeleton';
import { Button } from '@sara/ui/components/button';
import { QueryErrorBoundary } from '@sara/ui/components/query-error-boundary';
import { useActiveOrganization, useOrgSlug } from '../../contexts/auth';
import { CandidateDetailContainer } from '../../contexts/candidates/containers/CandidateDetailContainer';
import { candidatesHomeSearch } from '../../router/tree';

export function CandidateDetailPage() {
  return (
    <QueryErrorBoundary>
      <CandidateDetailPageContent />
    </QueryErrorBoundary>
  );
}

function CandidateDetailPageContent() {
  const { candidateId } = useParams({
    from: '/authenticated/orgs/$orgSlug/candidates/$candidateId',
  });
  const orgSlug = useOrgSlug();
  const { organization } = useActiveOrganization();
  const { data, isLoading, error } = useGetCandidate(candidateId, {
    query: { placeholderData: keepPreviousData },
  });

  if (isLoading) {
    return (
      <div className="space-y-12">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-6 w-96" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  // Only show "not found" for 404s; bubble everything else to QueryErrorBoundary.
  if (error) {
    if (error instanceof ApiError && error.status === 404) {
      // fall through to the not-found UI below
    } else {
      throw error;
    }
  }

  if (error || !data) {
    return (
      <div className="space-y-6 py-24 text-center">
        <h1 className="font-display text-[28px] font-medium tracking-tight text-foreground">
          Candidate not found
        </h1>
        <p className="text-sm text-muted-foreground">
          This candidate doesn&apos;t exist or you don&apos;t have access.
        </p>
        <Button asChild variant="outline" shape="pill" size="sm">
          <Link to="/orgs/$orgSlug/candidates" params={{ orgSlug }} search={candidatesHomeSearch}>
            Back to candidates
          </Link>
        </Button>
      </div>
    );
  }

  if (!organization) return null;

  return <CandidateDetailContainer orgId={organization.id} candidate={data} />;
}
